import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FaTimes,
  FaShieldAlt,
  FaCheckCircle,
  FaQuestionCircle,
  FaPlayCircle,
  FaBug,
  FaListOl
} from 'react-icons/fa';
import './FrictionEvidenceDrawer.css';

/**
 * Slide-out evidence drawer for inspection of backend-derived friction evidence.
 * Displays detection rule ID/version, reasoning, confidence/uncertainty, and supporting event IDs.
 * (API #19)
 */
export default function FrictionEvidenceDrawer({
  isOpen,
  onClose,
  evidence,
  signalMeta,
  isLoading = false,
  error = null,
  hasSessionReplayFeature = false,
  onWatchReplay
}) {
  const effectiveSampleSessionIds = useMemo(() => {
    if (Array.isArray(evidence?.sampleSessionIds) && evidence.sampleSessionIds.length > 0) {
      return evidence.sampleSessionIds;
    }
    const supporting = evidence?.supportingEventIds || signalMeta?.supportingEventIds;
    if (Array.isArray(supporting) && supporting.length > 0) {
      const extracted = supporting.map((id) =>
        id.includes(':') ? id.split(':').slice(1).join(':') : id
      );
      return Array.from(new Set(extracted.filter(Boolean)));
    }
    return [];
  }, [evidence, signalMeta]);

  if (!isOpen) return null;

  const confidencePercent = evidence?.confidence != null
    ? (typeof evidence.confidence === 'number'
        ? (evidence.confidence <= 1 ? Math.round(evidence.confidence * 100) : Math.round(evidence.confidence))
        : (typeof evidence.confidence === 'string'
            ? (evidence.confidence.toLowerCase() === 'high' ? 90 : evidence.confidence.toLowerCase() === 'medium' ? 60 : 30)
            : null))
    : null;

  const uncertaintyPercent = evidence?.uncertainty != null
    ? (typeof evidence.uncertainty === 'number'
        ? (evidence.uncertainty <= 1 ? Math.round(evidence.uncertainty * 100) : Math.round(evidence.uncertainty))
        : (typeof evidence.uncertainty === 'string' ? 20 : null))
    : null;

  const formatSignalType = (type) => {
    switch (type) {
      case 'rage_click':
        return 'Rage Click Anomaly';
      case 'dead_click':
        return 'Dead Click / Unresponsive Element';
      case 'stall':
        return 'Interaction Stall / Hesitation';
      case 'repeated_validation':
        return 'Repeated Field Validation Failure';
      default:
        return type || 'Friction Signal';
    }
  };

  return (
    <div className="evidence-drawer-overlay" data-testid="evidence-drawer-overlay" onClick={onClose}>
      <aside
        className="evidence-drawer"
        data-testid="friction-evidence-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {/* Drawer Header */}
        <div className="drawer-header">
          <div>
            <span className="drawer-kicker">Forensic Inspection (API #19)</span>
            <h3 id="drawer-title" className="drawer-title">
              {formatSignalType(signalMeta?.signalType || evidence?.signalType)}
            </h3>
          </div>
          <button
            type="button"
            className="drawer-close-btn"
            onClick={onClose}
            aria-label="Close evidence drawer"
            data-testid="close-evidence-drawer-btn"
          >
            <FaTimes />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="drawer-body">
          {isLoading && (
            <div className="drawer-loading-state" data-testid="evidence-loading">
              <div className="spinner" />
              <p>Retrieving anomaly telemetry & rule evidence...</p>
            </div>
          )}

          {error && (
            <div className="drawer-error-state" role="alert">
              <FaBug className="error-icon" />
              <p>{error}</p>
            </div>
          )}

          {!isLoading && !error && evidence && (
            <div className="evidence-content" data-testid="evidence-details">
              {/* Rule Card */}
              <div className="evidence-section rule-card">
                <div className="section-title-wrap">
                  <FaShieldAlt className="section-icon blue" />
                  <h4>Algorithmic Detection Rule</h4>
                </div>
                <div className="rule-meta-grid">
                  <div className="rule-meta-item">
                    <span className="rule-meta-label">Rule Identifier</span>
                    <code className="rule-meta-code" data-testid="evidence-rule-id">
                      {evidence.ruleId || 'RULE_UNSPECIFIED'}
                    </code>
                  </div>
                  <div className="rule-meta-item">
                    <span className="rule-meta-label">Engine Version</span>
                    <code className="rule-meta-code" data-testid="evidence-rule-version">
                      v{evidence.ruleVersion || '1.0.0'}
                    </code>
                  </div>
                </div>

                <div className="rule-reason-box">
                  <span className="reason-label">Heuristic Justification:</span>
                  <p className="reason-text" data-testid="evidence-reason">
                    {evidence.reason || 'Observed interaction cadence exceeded acceptable behavioural thresholds without state progression.'}
                  </p>
                </div>
              </div>

              {/* Confidence & Uncertainty Metrics */}
              <div className="evidence-section confidence-card">
                <h4>Inference Confidence & Calibration</h4>
                <div className="confidence-bars-group">
                  <div className="meter-wrapper">
                    <div className="meter-label-row">
                      <span><FaCheckCircle className="green-icon" /> Model Confidence</span>
                      <strong data-testid="evidence-confidence">
                        {confidencePercent != null ? `${confidencePercent}%` : 'N/A'}
                      </strong>
                    </div>
                    <div className="meter-track">
                      <div
                        className="meter-fill green"
                        style={{ width: `${confidencePercent || 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="meter-wrapper">
                    <div className="meter-label-row">
                      <span><FaQuestionCircle className="amber-icon" /> Estimated Uncertainty</span>
                      <strong data-testid="evidence-uncertainty">
                        {uncertaintyPercent != null ? `${uncertaintyPercent}%` : 'N/A'}
                      </strong>
                    </div>
                    <div className="meter-track">
                      <div
                        className="meter-fill amber"
                        style={{ width: `${uncertaintyPercent || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Target & Scope Details */}
              <div className="evidence-section target-card">
                <h4>Signal Scope & Target</h4>
                <div className="target-details-grid">
                  <div>
                    <span className="target-label">Target Selector:</span>
                    <code className="target-selector-code">
                      {evidence?.elementOrField || signalMeta?.elementOrField || evidence?.targetSelector || signalMeta?.targetSelector || 'N/A'}
                    </code>
                  </div>
                  <div>
                    <span className="target-label">Page Context:</span>
                    <span className="target-value">{signalMeta?.pageId || evidence?.pageId || 'Active Surface'}</span>
                  </div>
                  <div>
                    <span className="target-label">Observed Anomaly Count:</span>
                    <span className="target-value">
                      {(signalMeta?.count ?? signalMeta?.supportingEventIds?.length ?? evidence?.supportingEventIds?.length ?? 1).toLocaleString()} occurrences
                    </span>
                  </div>
                </div>
              </div>

              {/* Supporting Event Identifiers */}
              <div className="evidence-section events-card">
                <div className="section-title-wrap">
                  <FaListOl className="section-icon purple" />
                  <h4>Supporting Event Identifiers ({evidence.supportingEventIds?.length || 0})</h4>
                </div>
                <p className="events-sub">
                  Cryptographically referenced observation events that triggered this rule evaluation:
                </p>

                {Array.isArray(evidence.supportingEventIds) && evidence.supportingEventIds.length > 0 ? (
                  <div className="event-pills-list" data-testid="supporting-event-ids-list">
                    {evidence.supportingEventIds.map((eventId) => (
                      <span key={eventId} className="event-pill" data-testid={`event-pill-${eventId}`}>
                        {eventId}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="no-events-note">No raw supporting event IDs returned.</p>
                )}
              </div>

              {/* Sample Sessions for Replay Investigation */}
              {effectiveSampleSessionIds.length > 0 && (
                <div className="evidence-section sessions-card" data-testid="evidence-sessions-section">
                  <h4>Correlated User Sessions</h4>
                  <p className="sessions-sub">
                    Sessions where this interaction friction was captured:
                  </p>
                  <div className="sample-sessions-list">
                    {effectiveSampleSessionIds.map((sessId) => (
                      <div key={sessId} className="session-item-row" data-testid={`sample-session-${sessId}`}>
                        <code className="session-id-tag">{sessId}</code>
                        {hasSessionReplayFeature && onWatchReplay && (
                          <button
                            type="button"
                            className="watch-replay-btn"
                            onClick={() => onWatchReplay(sessId)}
                            data-testid={`watch-replay-btn-${sessId}`}
                          >
                            <FaPlayCircle /> Playback Replay
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

FrictionEvidenceDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  evidence: PropTypes.shape({
    frictionId: PropTypes.string,
    signalType: PropTypes.string,
    ruleId: PropTypes.string,
    ruleVersion: PropTypes.string,
    reason: PropTypes.string,
    confidence: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    uncertainty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    targetSelector: PropTypes.string,
    elementOrField: PropTypes.string,
    pageId: PropTypes.string,
    supportingEventIds: PropTypes.arrayOf(PropTypes.string),
    sampleSessionIds: PropTypes.arrayOf(PropTypes.string)
  }),
  signalMeta: PropTypes.shape({
    frictionId: PropTypes.string,
    signalType: PropTypes.string,
    targetSelector: PropTypes.string,
    elementOrField: PropTypes.string,
    pageId: PropTypes.string,
    count: PropTypes.number
  }),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  hasSessionReplayFeature: PropTypes.bool,
  onWatchReplay: PropTypes.func
};
